import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import parse from 'html-react-parser'
import { useAuth } from '../../context/AuthContext'
import { chatApi } from '../../api/chat'
import Button from '../common/Button'
import Input from '../common/Input'
import './ChatbotWidget.css'

// Helper function to detect HTML content
const containsHtml = (text) => {
  if (typeof text !== 'string') return false
  const htmlPattern = /<[^>]+>/
  return htmlPattern.test(text)
}

// Message Content Component - handles both HTML and Markdown
const MessageContent = ({ content, hasNoAnswer = false }) => {
  if (!content) return null
  
  const isHtml = containsHtml(content)
  
  if (isHtml) {
    return (
      <div className={`chatbot-widget-message-html-content ${hasNoAnswer ? 'no-answer' : ''}`}>
        {parse(content)}
      </div>
    )
  }
  
  return <ReactMarkdown>{content}</ReactMarkdown>
}

// Thinking Indicator Component
const ThinkingIndicator = () => {
  return (
    <div className="chatbot-widget-thinking-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  )
}

// Typewriter Message Component (only for non-HTML content)
const TypewriterMessage = ({ children, messageId, onComplete, hasNoAnswer = false }) => {
  const [displayedText, setDisplayedText] = useState('')
  const fullText = typeof children === 'string' ? children : ''
  const isHtml = containsHtml(fullText)

  useEffect(() => {
    if (isHtml || !fullText) {
      setDisplayedText(fullText)
      if (onComplete) onComplete()
      return
    }

    setDisplayedText('')
    let currentIndex = 0
    const interval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(interval)
        if (onComplete) onComplete()
      }
    }, 20) // Adjust speed as needed

    return () => clearInterval(interval)
  }, [messageId, fullText, isHtml, onComplete])

  if (isHtml || !fullText) {
    return <MessageContent content={fullText} hasNoAnswer={hasNoAnswer} />
  }

  return <ReactMarkdown>{displayedText}</ReactMarkdown>
}

const ChatbotWidget = () => {
  const { isAuthenticated } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [currentFlowId, setCurrentFlowId] = useState(null)
  const [messages, setMessages] = useState([])
  const [animatedMessageIds, setAnimatedMessageIds] = useState(new Set())
  const messagesEndRef = useRef(null)
  const queryClient = useQueryClient()
  const locale = localStorage.getItem('locale') || 'ar'

  // All hooks must be called before any conditional returns
  const { data: flowsData, isLoading: flowsLoading } = useQuery({
    queryKey: ['chat-flows'],
    queryFn: () => chatApi.getFlows(),
    enabled: isOpen && isAuthenticated, // Only fetch when widget is open and user is authenticated
  })

  const { data: knowledgeBaseQuestions = [], isLoading: kbLoading } = useQuery({
    queryKey: ['knowledge-base-questions-widget', locale],
    queryFn: () => chatApi.getKnowledgeBaseQuestions('website', locale),
    enabled: isOpen && isAuthenticated, // Only fetch when widget is open and user is authenticated
  })

  const sendMutation = useMutation({
    mutationFn: (data) => chatApi.sendMessage(data),
    onSuccess: (response, variables) => {
      // Handle response format: { success: true, data: {...} } or direct data
      const responseData = response?.data || response

      // Add user message
      if (variables.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: 'user',
            message: variables.message,
            timestamp: new Date().toISOString(),
          },
        ])
      }

      // Add bot response
      if (responseData?.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'bot',
            message: responseData.message,
            hasAnswer: responseData.has_answer !== false,
            responses: responseData.responses || [],
            sourceType: responseData.source_type,
            actions: responseData.actions || [],
            searchResults: responseData.search_results,
            timestamp: new Date().toISOString(),
          },
        ])

        // Update flow ID if provided
        if (responseData.next_flow_id) {
          setCurrentFlowId(responseData.next_flow_id)
        }
      }
    },
    onError: (error) => {
      console.error('Chat error:', error)
      setMessages((prev) => [
        ...prev,
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'bot',
          message: locale === 'ar' 
            ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
            : 'Sorry, an error occurred. Please try again.',
          hasAnswer: false,
          timestamp: new Date().toISOString(),
        },
      ])
      ])
    },
  })

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, sendMutation.isPending, isOpen])

  // Clear messages when opening widget
  useEffect(() => {
    if (isOpen) {
      setMessages([])
      setCurrentFlowId(null)
      setAnimatedMessageIds(new Set())
    }
  }, [isOpen])

  const flows = Array.isArray(flowsData) ? flowsData : flowsData?.flows || []
  const rootFlow = flows.find((f) => f.is_root === true || f.is_root === 1) || flows[0]
  const frequentQuestions = knowledgeBaseQuestions || []

  const handleSend = (e) => {
    e.preventDefault()
    if (!message.trim() || sendMutation.isPending) return

    sendMutation.mutate({
      message: message.trim(),
      flowId: currentFlowId,
    })
    setMessage('')
  }

  const handleFrequentQuestion = (kbQuestion) => {
    if (sendMutation.isPending) return
    const questionId = kbQuestion?.id
    const questionText = kbQuestion?.question || kbQuestion
    
    sendMutation.mutate({
      message: questionText,
      flowId: currentFlowId,
      knowledgeBaseId: questionId,
    })
  }

  const handleRestartChat = () => {
    if (sendMutation.isPending) return
    
    // Clear messages
    setMessages([])
    // Reset flow ID
    setCurrentFlowId(null)
    // Clear animated message IDs
    setAnimatedMessageIds(new Set())
    // Reload chat flows and knowledge base questions
    queryClient.invalidateQueries(['chat-flows'])
    queryClient.invalidateQueries(['knowledge-base-questions-widget', locale])
    // Scroll to top
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleFlowOption = (option) => {
    if (sendMutation.isPending) return
    const optionText = option.text || option.response_text || ''
    const nextFlowId = option.next_flow_id || option.action_value

    if (nextFlowId) {
      setCurrentFlowId(nextFlowId)
    }

    sendMutation.mutate({
      message: optionText,
      flowId: nextFlowId || currentFlowId,
    })
  }

  const handleActionClick = (action) => {
    // Handle action buttons (navigate to course, lecture, etc.)
    if (action.type === 'course') {
      window.location.href = `/courses/${action.id}`
    } else if (action.type === 'lecture' && action.course_id) {
      window.location.href = `/courses/${action.course_id}/lectures/${action.id}`
    } else if (action.type === 'post') {
      window.location.href = `/community/posts/${action.id || action.post_id}`
    }
  }

  // Only show widget for authenticated users - check after all hooks
  if (!isAuthenticated) {
    return null
  }

  if (!isOpen) {
    return (
      <button
        className="chatbot-widget-button"
        onClick={() => setIsOpen(true)}
        aria-label={locale === 'ar' ? 'فتح الدردشة' : 'Open Chat'}
        title={locale === 'ar' ? 'دعم الدردشة' : 'Chat Support'}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>
    )
  }

  return (
    <div className="chatbot-widget-container">
      <div className="chatbot-widget-header">
        <div className="chatbot-widget-header-content">
          <div className="chatbot-widget-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div>
            <h3>{locale === 'ar' ? 'دعم الدردشة' : 'Chat Support'}</h3>
            <p>{locale === 'ar' ? 'مساعد ذكي' : 'AI Assistant'}</p>
          </div>
        </div>
        <div className="chatbot-widget-header-actions">
          <button
            className="chatbot-widget-restart"
            onClick={handleRestartChat}
            disabled={sendMutation.isPending}
            title={locale === 'ar' ? 'بدء محادثة جديدة' : 'New Conversation'}
            aria-label={locale === 'ar' ? 'بدء محادثة جديدة' : 'New Conversation'}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
          </button>
          <button
            className="chatbot-widget-close"
            onClick={() => setIsOpen(false)}
            aria-label={locale === 'ar' ? 'إغلاق' : 'Close'}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <div className="chatbot-widget-messages">
        {flowsLoading && messages.length === 0 && (
          <div className="chatbot-widget-loading">
            <div className="loading-spinner"></div>
          </div>
        )}

        {!flowsLoading && messages.length === 0 && rootFlow && (
          <div className="chatbot-widget-welcome">
            {rootFlow.message && (
              <div className="chatbot-widget-welcome-message">
                <MessageContent content={rootFlow.message} />
              </div>
            )}

            {frequentQuestions.length > 0 && (
              <div className="chatbot-widget-frequent-questions">
                <h4>{locale === 'ar' ? 'الأسئلة المتكررة' : 'Frequent Questions'}</h4>
                <div className="chatbot-widget-frequent-list">
                  {frequentQuestions.map((kbQuestion, idx) => {
                    // Handle both object format (from backend) and string format
                    const questionId = kbQuestion?.id
                    const questionText = kbQuestion?.question || kbQuestion
                    // Strip HTML tags for display in button
                    const displayText = typeof questionText === 'string' 
                      ? questionText.replace(/<[^>]+>/g, '').trim() 
                      : questionText
                    
                    return (
                      <button
                        key={questionId || idx}
                        className="chatbot-widget-frequent-chip"
                        onClick={() => handleFrequentQuestion(kbQuestion)}
                        disabled={sendMutation.isPending}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
                        </svg>
                        <span>{displayText}</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {rootFlow.responses && rootFlow.responses.length > 0 && (
              <div className="chatbot-widget-flow-options">
                <p>{locale === 'ar' ? 'اختر خيارًا:' : 'Select an option:'}</p>
                {rootFlow.responses.map((response, idx) => (
                  <button
                    key={idx}
                    className="chatbot-widget-option-button"
                    onClick={() => handleFlowOption(response)}
                    disabled={sendMutation.isPending}
                  >
                    {response.text || response.response_text}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={msg.id || idx}
            className={`chatbot-widget-message ${
              msg.type === 'user' ? 'user' : 'bot'
            } ${msg.hasAnswer === false ? 'no-answer' : ''}`}
          >
            {msg.sourceType === 'database_search' && (
              <div className="chatbot-widget-message-source">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>
                  {locale === 'ar' ? 'تم العثور عليه في قاعدة البيانات' : 'Found in database'}
                </span>
              </div>
            )}

            <div className="chatbot-widget-message-content">
              {msg.type === 'user' ? (
                <p>{msg.message}</p>
              ) : (
                <>
                  {animatedMessageIds.has(msg.id) || containsHtml(msg.message) ? (
                    <MessageContent content={msg.message} hasNoAnswer={msg.hasAnswer === false} />
                  ) : (
                    <TypewriterMessage
                      key={`typewriter-${msg.id}`}
                      messageId={msg.id}
                      hasNoAnswer={msg.hasAnswer === false}
                      onComplete={() => {
                        if (msg.id) {
                          setAnimatedMessageIds((prev) => {
                            const newSet = new Set(prev)
                            newSet.add(msg.id)
                            return newSet
                          })
                        }
                      }}
                    >
                      {msg.message}
                    </TypewriterMessage>
                  )}
                </>
              )}
            </div>

            {msg.actions && msg.actions.length > 0 && (
              <div className="chatbot-widget-message-actions">
                {msg.actions.map((action, actionIdx) => (
                  <button
                    key={actionIdx}
                    className="chatbot-widget-action-button"
                    onClick={() => handleActionClick(action)}
                  >
                    {action.title || action.type}
                  </button>
                ))}
              </div>
            )}

            {msg.hasAnswer === false && msg.responses && msg.responses.length > 0 && (
              <div className="chatbot-widget-message-responses">
                <p className="chatbot-widget-responses-label">
                  {locale === 'ar' ? 'يرجى اختيار خيار:' : 'Please select an option:'}
                </p>
                {msg.responses.map((response, respIdx) => (
                  <button
                    key={respIdx}
                    className="chatbot-widget-response-option"
                    onClick={() => handleFlowOption(response)}
                    disabled={sendMutation.isPending}
                  >
                    {response.text || response.response_text}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {sendMutation.isPending && (
          <div className="chatbot-widget-message bot typing">
            <ThinkingIndicator />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="chatbot-widget-input-form">
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={locale === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
          fullWidth
          disabled={sendMutation.isPending}
        />
        <Button
          type="submit"
          variant="primary"
          disabled={!message.trim() || sendMutation.isPending}
          loading={sendMutation.isPending}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </Button>
      </form>
    </div>
  )
}

export default ChatbotWidget

